"use client";

import * as React from "react";
import { Heart } from "lucide-react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { createClient } from "@/lib/supabase/client";
import { cn } from "@/lib/utils";

export default function FavoriteToggle({
  listingId,
  className,
  onToggle,
  showLabel = false,
}: {
  listingId: string;
  className?: string;
  onToggle?: (isFav: boolean) => void;
  /** afișează și textul („Salvează” / „Salvat”) — varianta din pagina de anunț */
  showLabel?: boolean;
}) {
  const router = useRouter();
  const [authed, setAuthed] = React.useState<boolean | null>(null);
  const [isFav, setIsFav] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const supabase = React.useMemo(() => createClient(), []);
  const favoriteLoadedRef = React.useRef(false);

  React.useEffect(() => {
    favoriteLoadedRef.current = false;
    setAuthed(null);
    setIsFav(false);
  }, [listingId]);

  const loadFavoriteState = React.useCallback(async () => {
    if (favoriteLoadedRef.current) {
      return { authed, isFav };
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        favoriteLoadedRef.current = true;
        setAuthed(false);
        setIsFav(false);
        return { authed: false, isFav: false };
      }

      const res = await fetch(`/api/favorites?listing_id=${encodeURIComponent(listingId)}`, {
        cache: "no-store",
      });
      const json = res.ok ? await res.json().catch(() => ({})) : {};
      const nextIsFav = Boolean(json?.isFavorite);

      favoriteLoadedRef.current = true;
      setAuthed(true);
      setIsFav(nextIsFav);

      return { authed: true, isFav: nextIsFav };
    } catch {
      setAuthed(null);
      setIsFav(false);
      return { authed: null, isFav: false };
    }
  }, [authed, isFav, listingId, supabase]);

  const toggle = async () => {
    if (busy) return;

    const favoriteState = await loadFavoriteState();

    if (!favoriteState.authed) {
      router.push("/autentificare");
      return;
    }

    try {
      setBusy(true);

      if (favoriteState.isFav) {
        const res = await fetch("/api/favorites", {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ listing_id: listingId }),
        });

        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.error || "Failed to remove favorite");
        }

        favoriteLoadedRef.current = true;
        setIsFav(false);
        toast.success("Eliminat din favorite");

        if (onToggle) onToggle(false);
        else router.refresh();
      } else {
        const res = await fetch("/api/favorites", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ listing_id: listingId }),
        });

        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.error || "Failed to add favorite");
        }

        favoriteLoadedRef.current = true;
        setIsFav(true);
        toast.success("Adăugat la favorite");
        if (onToggle) onToggle(true);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Eroare la actualizarea favorite";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const active = isFav === true;

  const label = active ? "Elimină din favorite" : "Adaugă la favorite";

  if (showLabel) {
    return (
      <button
        type="button"
        aria-label={label}
        aria-pressed={active}
        onClick={toggle}
        onFocus={() => { void loadFavoriteState(); }}
        onPointerEnter={() => { void loadFavoriteState(); }}
        disabled={busy}
        className={cn(
          "inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-border bg-card px-4 font-display text-sm font-semibold transition-colors hover:bg-muted disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
          active ? "text-red-600" : "text-foreground",
          className
        )}
      >
        <Heart className={cn("size-4", active && "fill-current")} aria-hidden="true" />
        {active ? "Salvat la favorite" : "Salvează la favorite"}
      </button>
    );
  }

  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onClick={toggle}
      onFocus={() => { void loadFavoriteState(); }}
      onPointerEnter={() => { void loadFavoriteState(); }}
      disabled={busy}
      className={cn(
        "inline-flex size-9 cursor-pointer items-center justify-center rounded-full border border-border bg-card/90 shadow-xs backdrop-blur transition-colors hover:bg-card disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
        active ? "text-red-600" : "text-foreground",
        className
      )}
    >
      <Heart className={cn("size-4", active && "fill-current")} aria-hidden="true" />
    </button>
  );
}
